<template>
    <div v-if="statsigReady && hasAiImagesAccess" class="card mt-4 px-0">
        <div class="card-header px-4">
            <h3>Image Generation</h3>
            <p>Manage your organization's AI image policy and the library of images. These images will be used as references for AI-generation or editing for playbook touchpoints.</p>

            <!-- Image Library (above dropdown and instructions; only show when saved preference is not 'none') -->
            <div v-if="(client.image_preference || 'none') !== 'none'" class="row mt-3">
                <div class="col-md-12 col-12">
                    <div class="d-flex justify-content-between align-items-center">
                        <h4 class="mb-0">Your Image Library</h4>
                        <div class="d-flex gap-2 position-relative">
                            <input 
                                type="text" 
                                class="search-box form-control me-1 py-2" 
                                placeholder="Search by name..." 
                                aria-label="Search by name..." 
                                v-model="search_key" 
                                @input="debouncedSearch"
                            >
                            <i class="fa fa-search search-placeholder-icon" v-if="search_key === ''"></i>
                            <button class="btn btn-secondary me-1 py-0 dropdown-toggle dropdown-toggle-no-btn" data-bs-toggle="dropdown" data-bs-auto-close="outside" aria-expanded="false">
                                <img class="icon" src="/img/icons/avid-icons/filter-funnel-01.svg" alt="">Filters ({{ activeFilterCount }})
                            </button>
                            <div class="dropdown-menu dropdown-menu-primary dropdown-menu-end p-3" style="max-height: 450px; overflow-y: scroll;">
                                <div v-if="activeFilterCount > 0" class="mb-3">
                                    <a href="#" @click.prevent="clearAllFilters" class="text-primary">
                                        <i class="fa fa-times me-1"></i>Clear All Filters
                                    </a>
                                    <hr class="my-2">
                                </div>
                                <label class="form-label">Classification:</label>
                                <select 
                                    class="form-select mb-2" 
                                    v-model="selectedClassification"
                                    @change="selectClassification(selectedClassification)"
                                 aria-label="Classification:">
                                    <option value="all">All</option>
                                    <option value="mission_in_action">Mission-in-Action</option>
                                    <option value="location">Location</option>
                                    <option value="products">Products</option>
                                    <option value="logo">Logo</option>
                                    <option value="design_styles">Design Styles</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>
                            <button 
                                v-if="canEdit('settings')"
                                class="btn btn-primary mt-2 mt-md-0 text-nowrap" 
                                @click="openUploadModal" 
                                :disabled="uploading"
                            >
                                <img src="/img/icons/avid-icons/upload.svg" alt="Upload" class="icon me-2">
                                Upload Image
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="(client.image_preference || 'none') !== 'none'" class="py-2">
            <!-- Images Grid -->
            <div v-if="images.length > 0" class="px-0">
                <div class="d-flex flex-wrap gap-2 align-items-start">
                    <div v-for="image in images" :key="image.id" class="position-relative image-thumbnail-wrapper" style="max-width: 200px;">
                        <img 
                            v-if="getImageUrl(image)" 
                            :src="getImageUrl(image)" 
                            :alt="image.name"
                            class="image-thumbnail"
                            style="height: 100px; width: auto; max-width: 100%; object-fit: contain; border-radius: 20px; cursor: pointer;"
                            @click="editImage(image)"
                            data-bs-toggle="tooltip"
                            :title="image.name"
                        >
                        <div v-else class="d-flex align-items-center justify-content-center position-relative" style="height: 100px; width: 100px; background: #f0f0f0; border-radius: 20px; cursor: pointer;" @click="editImage(image)">
                            <span class="text-muted small">No preview</span>
                        </div>
                        <div v-if="canEdit('settings')" class="position-absolute top-0 end-0 d-flex gap-1 m-1">
                            <button 
                                type="button" 
                                class="btn btn-icon-only-sm btn-secondary p-1" 
                                data-bs-toggle="tooltip" 
                                title="Delete"
                                @click.stop.prevent="deleteImage(image)"
                                style="display: flex; align-items: center; justify-content: center;"
                            >
                                <img class="icon" src="/img/icons/avid-icons/trash.svg" alt="">
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Pagination -->
                <div class="table-pagination mt-4 mb-0">
                    <div class="table-pagination--group d-none d-md-flex">
                        <div class="position-relative">
                            <select class="form-select" name="num_per_page" id="num_per_page" v-model="num_per_page" @change="reloadPagination()" aria-label="Num Per Page">
                                <option value="5000">ALL</option>
                                <option v-for="num in num_per_page_options" :key="num" :value="num">{{ num }}</option>
                            </select>
                            <div class="select-display-text">
                                Showing {{ num_per_page === 5000 ? 'ALL' : num_per_page }}
                            </div>
                        </div>
                    </div>

                    <div class="table-pagination--group d-none d-lg-flex">
                        {{ local_num_images == 0 ? '0' : item_interval }} of {{ local_num_images }}
                    </div>

                    <div class="table-pagination--group" v-if="page_count > 1">
                        <button class="btn btn-secondary btn-icon-only-md me-1" @click="moveToPrevPage()" :disabled="page_index == 0" data-bs-toggle="tooltip" title="Previous page">
                            <img class="icon" src="/img/icons/avid-icons/chevron-left.svg" alt="Previous">
                        </button>

                        <div class="d-flex page-selector" v-if="page_count <= 3">
                            <button v-for="page in page_index_array" :key="page" class="btn btn-none btn-icon-only-md" :class="page == page_index + 1 ? 'current-page':''" @click="moveToPage(page)">
                                {{ page }}
                            </button>
                        </div>
                        <div class="d-flex page-selector" v-else>
                            <button v-for="page in page_index_array.slice(0,2)" :key="page" class="btn btn-none btn-icon-only-md" :class="page == page_index + 1 ? 'current-page':''" @click="moveToPage(page)">
                                {{ page }}
                            </button>
                            <button v-if="page_index > 2" class="btn btn-none btn-icon-only-md text-nowrap" disabled>...</button>
                            <button v-if="page_index >= 2 && page_index < page_count - 1" class="btn btn-none btn-icon-only-md current-page">
                                {{ page_index + 1 }}
                            </button>
                            <button v-if="page_index < page_count - 2" class="btn btn-none btn-icon-only-md text-nowrap" disabled aria-hidden="true">...</button>
                            <button class="btn btn-none btn-icon-only-md" :class="page_index == page_count - 1 ? 'current-page':''" @click="moveToPage(page_count)">
                                {{ page_count }}
                            </button>
                        </div>

                        <button class="btn btn-secondary btn-icon-only-md ms-1" @click="moveToNextPage()" :disabled="page_index >= page_count - 1" data-bs-toggle="tooltip" title="Next page">
                            <img class="icon" src="/img/icons/avid-icons/chevron-right.svg" alt="Next">
                        </button>
                    </div>
                </div>
            </div>
            <div v-else class="text-muted fst-italic mb-3">No images have been added to the library. <a href="#" @click.prevent="openUploadModal" v-if="canEdit('settings')">Click here</a> to add some.</div>
            </div>

            <!-- Image Generation Preference -->
            <div class="mb-3 row mt-3">
                <div class="col col-sm-6">
                    <label for="imagePreference" class="form-label">Image Generation Preference</label>
                    <select 
                        class="form-select" 
                        id="imagePreference" 
                        v-model="local_image_preference"
                        :disabled="!canEdit('settings')"
                     aria-label="Image Generation Preference">
                        <option value="none">No image generation</option>
                        <option value="style_text_only">Style & text edits only</option>
                        <option value="ai_allowed">AI images are allowed</option>
                    </select>
                </div>
            </div>

            <div v-if="(client.image_preference || 'none') === 'ai_allowed'" class="mb-3 row mt-3">
                <div class="col col-sm-12">
                    <label for="imageGenerationInstructions" class="form-label">Image Generation Instructions</label>
                    <textarea
                        class="form-control"
                        id="imageGenerationInstructions"
                        v-model="local_image_generation_instructions"
                        rows="3"
                        :placeholder="imageGenerationInstructionsPlaceholder"
                        :disabled="!canEdit('settings')"
                    ></textarea>
                    <p class="form-text small text-muted mt-1 mb-0">These instructions will be included for every image that is generated or edited.</p>
                </div>
            </div>

            <div v-if="canEdit('settings')" class="row mt-3">
                <div class="col">
                    <button 
                        class="btn btn-primary" 
                        @click="updateImagePreference"
                        :disabled="!imageSettingsDirty || savingPreference"
                    >
                        <img src="/img/icons/avid-icons/save.svg" alt="Save" class="icon me-2">Save
                    </button>
                </div>
            </div>
        </div>
    </div>

    <!-- Upload/Edit Modal -->
    <div class="modal fade" id="imageModal" tabindex="-1" aria-labelledby="imageModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-lg">
            <div class="modal-content">
                <div class="modal-header border-bottom-0 pb-0">
                    <div class="w-100 position-relative">
                        <button type="button" class="btn btn-white position-absolute top-0 end-0 close-modal" data-bs-dismiss="modal" style="z-index: 10;" data-bs-toggle="tooltip" title="Close">
                            <img class="icon me-0" src="/img/icons/avid-icons/close.svg" alt=""> 
                        </button>
                        <h3 class="mt-2 pe-5">{{ modal_action === 'edit' ? 'Edit Image' : 'Upload Image' }}</h3>
                    </div>
                </div>
                <div class="modal-body">
                    <div v-if="uploading" class="text-center">
                        <div class="spinner-border" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                        <p class="mt-2">{{ modal_action === 'edit' ? 'Saving image...' : (uploadingCount === 1 ? 'Uploading image...' : 'Uploading images...') }}</p>
                    </div>
                    <div v-else>
                        <!-- Image Preview (Edit Mode) -->
                        <div v-if="modal_action === 'edit' && getImageUrl(modal_image)" class="mb-4 d-flex justify-content-center align-items-center" style="min-height: 300px;">
                            <img 
                                :src="getImageUrl(modal_image)" 
                                :alt="modal_image.name"
                                class="img-fluid"
                                style="max-width: 100%; max-height: 100vh; object-fit: contain; border-radius: 20px;"
                            />
                        </div>
                        <div v-if="modal_action === 'upload'">
                            <label class="form-label">Upload new images</label>
                            <ImageDropzone
                                :key="'image-library-dropzone-' + uploadDropzoneKey"
                                v-model="uploadImageUrl"
                                id="image-library-dropzone"
                                :clientUrl="client?.url"
                                :max-files="20"
                                :min-height="140"
                                :if_thumbnail="false"
                                :hide-delete-button="true"
                                @update:modelValue="handleUploadFileUploaded"
                                @file-uploaded="handleUploadFileUploaded"
                                @upload-progress="handleUploadProgress"
                            />
                            <div v-if="uploadProgress" class="alert alert-light border d-flex align-items-center gap-2 mt-2">
                                <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
                                <span class="small text-muted">
                                    Uploading {{ uploadProgress.fileName }}
                                    <span v-if="uploadProgress.totalFiles > 1">
                                        ({{ uploadProgress.fileIndex }} of {{ uploadProgress.totalFiles }})
                                    </span>
                                    — {{ uploadProgress.percent }}%
                                </span>
                                <div class="progress flex-grow-1" style="height: 6px;">
                                    <div
                                        class="progress-bar"
                                        role="progressbar"
                                        :style="{ width: uploadProgress.percent + '%' }"
                                    ></div>
                                </div>
                            </div>
                            <div v-if="uploadPendingUrls.length > 0" class="d-flex flex-wrap gap-2 mt-2">
                                <div
                                    v-for="(item, idx) in uploadPendingUrls"
                                    :key="item.key"
                                    class="position-relative image-library-upload-thumb"
                                >
                                    <img :src="normalizeUploadUrl(item.url)" alt="Upload" class="rounded">
                                    <button
                                        type="button"
                                        class="btn btn-sm btn-white position-absolute top-0 end-0 m-1 shadow-sm"
                                        @click="removePendingUploadUrl(idx)"
                                        aria-label="Remove"
                                    >
                                        <img class="icon" src="/img/icons/avid-icons/trash.svg" alt="">
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div class="mb-3" v-if="modal_action === 'edit'">
                            <label for="imageName" class="form-label">Name *</label>
                            <input 
                                type="text" 
                                class="form-control" 
                                id="imageName" 
                                v-model="modal_image.name"
                             aria-label="Name *">
                        </div>
                        <div class="mb-3" v-if="modal_action === 'edit'">
                            <label for="imageDescription" class="form-label">Description</label>
                            <textarea 
                                ref="descriptionTextarea"
                                class="form-control" 
                                id="imageDescription" 
                                :rows="modal_action === 'edit' && modal_image.description ? Math.max(3, modal_image.description.split('\n').length) : 3"
                                v-model="modal_image.description"
                            ></textarea>
                        </div>
                        <div class="mb-3" v-if="modal_action === 'edit'">
                            <label for="imageClassification" class="form-label">Classification *</label>
                            <select 
                                class="form-select" 
                                id="imageClassification" 
                                v-model="modal_image.image_classification"
                             aria-label="Classification *">
                                <option value="mission_in_action">Mission-in-Action</option>
                                <option value="location">Location</option>
                                <option value="products">Products</option>
                                <option value="logo">Logo</option>
                                <option value="design_styles">Design Styles</option>
                                <option value="other">Other</option>
                            </select>
                        </div>
                        <div class="mb-3" v-if="modal_action === 'edit'">
                            <label for="imageKeywords" class="form-label">Keywords (comma-separated)</label>
                            <input 
                                type="text" 
                                class="form-control" 
                                id="imageKeywords" 
                                v-model="keywordsString"
                                placeholder="keyword1, keyword2, keyword3"
                             aria-label="Keywords (comma-separated)">
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                    <button 
                        type="button" 
                        class="btn btn-primary" 
                        @click="saveImage"
                        :disabled="uploading || (modal_action === 'upload' && uploadPendingUrls.length === 0) || (modal_action === 'edit' && (!modal_image.name || !modal_image.image_classification))"
                    >
                        {{ modal_action === 'edit' ? 'Update' : 'Upload' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import permissionsMixin from '../../../mixins/permissions';
import { useNotifications } from '../../../composables/useNotifications';
import Swal from 'sweetalert2';
import debounce from 'lodash.debounce';
import ImageDropzone from '../../components/ImageDropzone.vue';
import statsigService from '../../../services/statsig';

export default defineComponent({
    name: 'ImageLibrarySettings',
    mixins: [permissionsMixin],
    components: {
        ImageDropzone
    },
    setup() {
        const { showNotification } = useNotifications();
        return { showNotification };
    },
    props: {
        user: {
            type: Object,
            required: true
        },
        client: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            selectedClassification: 'all',
            images: [],
            uploading: false,
            uploadingCount: 1,
            savingPreference: false,
            search_key: '',
            local_image_preference: this.client.image_preference || 'none',
            local_image_generation_instructions: this.client.image_generation_instructions || '',
            // Pagination
            num_per_page: 25,
            num_per_page_options: [5, 10, 15, 20, 25, 30, 50, 100],
            page_count: null,
            page_index: 0,
            page_index_array: [],
            item_interval: '',
            local_num_images: 0,
            // Modal
            modal_action: 'upload',
            modal_image: {
                name: '',
                description: '',
                image_classification: 'mission_in_action',
                keywords: [],
                campaign_specific: false,
            },
            uploadImageUrl: '',
            uploadPendingUrls: [],
            uploadDropzoneKey: 0,
            uploadProgress: null,
            keywordsString: '',
            echoChannel: null,
            // Statsig feature gate tracking
            statsigReady: false,
            hasCheckedAiImagesGate: false,
            aiImagesGateResult: null,
        };
    },
    computed: {
        imageSettingsDirty() {
            const prefChanged = this.local_image_preference !== (this.client.image_preference || 'none');
            const instructionsChanged = (this.local_image_generation_instructions || '').trim() !== (this.client.image_generation_instructions || '').trim();
            return prefChanged || instructionsChanged;
        },
        imageGenerationInstructionsPlaceholder() {
            return 'e.g. Use warm, professional tones; avoid depicting real people; follow our brand colors. You cannot request specific individuals, copyrighted characters, or content that violates our content policy.';
        },
        activeFilterCount() {
            let count = 0;
            if (this.selectedClassification !== 'all') {
                count++;
            }
            return count;
        },
        hasAiImagesAccess() {
            // If we've already checked the gate, return the cached result
            if (this.hasCheckedAiImagesGate) {
                return this.aiImagesGateResult;
            }
            
            // Mark that we've checked the gate
            this.hasCheckedAiImagesGate = true;
            
            try {
                // Check the ai_images feature gate using the service
                const hasAccess = statsigService.checkGate('ai_images');
                
                // Cache the result
                this.aiImagesGateResult = hasAccess;
                
                return hasAccess;
            } catch (error) {
                // If there's an error, fall back to the default logic
                this.aiImagesGateResult = true;
                return true;
            }
        },
    },
    async mounted() {
        // Initialize Statsig and check feature gates
        try {
            await statsigService.initialize(this.user || {});
            
            this.aiImagesGateResult = statsigService.checkGate('ai_images');
            
            this.hasCheckedAiImagesGate = true;
        } catch (err) {
            console.error('Statsig failed to init, defaulting gates on:', err);
            this.aiImagesGateResult = true;
            this.hasCheckedAiImagesGate = true;
        } finally {
            this.statsigReady = true;
        }
        
        // Only fetch images if gate is enabled
        if (this.statsigReady && this.aiImagesGateResult) {
            this.fetchImages();
            this.debouncedSearch = debounce(this.searchImages, 500);
            
            // Listen for modal shown event to adjust textarea height
            const modalElement = document.getElementById('imageModal');
            if (modalElement) {
                modalElement.addEventListener('shown.bs.modal', () => {
                    this.adjustTextareaHeight();
                });
            }
            
            // Subscribe to Pusher events for client image processing
            this.subscribeToEvents();
        }
    },
    beforeUnmount() {
        // Unsubscribe from Pusher events
        if (window.Echo && this.client && this.client.id) {
            window.Echo.private('client.' + this.client.id).stopListening('ClientImageProcessed');
        }
    },
    methods: {
        selectClassification(classification) {
            this.selectedClassification = classification;
            this.page_index = 0;
            this.fetchImages();
        },
        
        async fetchImages() {
            try {
                const params = new URLSearchParams({
                    search: this.search_key || '',
                    classification: this.selectedClassification === 'all' ? '' : this.selectedClassification,
                    per_page: this.num_per_page === 5000 ? 10000 : this.num_per_page, // Use large number for "ALL"
                    page: this.page_index + 1,
                });
                
                const response = await window.axios.get(`/api/client/${this.client.url}/images?${params}`);
                
                // Handle Laravel pagination response
                if (response.data && response.data.data) {
                    this.images = response.data.data || [];
                    this.local_num_images = response.data.total || 0;
                    // Update page_index if it's out of bounds
                    if (this.page_index + 1 > response.data.last_page && response.data.last_page > 0) {
                        this.page_index = response.data.last_page - 1;
                    }
                } else {
                    // Fallback if response structure is different
                    this.images = Array.isArray(response.data) ? response.data : [];
                    this.local_num_images = this.images.length;
                }
                
                this.updatePagination();
            } catch (error) {
                console.error('Error fetching images:', error);
                this.images = [];
                this.local_num_images = 0;
                this.updatePagination();
            }
        },
        
        searchImages() {
            this.page_index = 0;
            this.fetchImages();
        },
        
        updatePagination() {
            if (this.num_per_page === 5000) {
                // "ALL" option - show all on one page
                this.page_count = 1;
                this.page_index_array = [1];
                this.page_index = 0;
            } else {
                this.page_count = Math.ceil(this.local_num_images / this.num_per_page);
                this.page_index_array = Array.from({length: this.page_count}, (_, i) => i + 1);
            }
            this.getItemInterval();
        },
        
        getItemInterval() {
            if (this.local_num_images > 0) {
                if (this.num_per_page === 5000) {
                    // "ALL" option - show total count
                    this.item_interval = `1 - ${this.local_num_images}`;
                } else {
                    var from = this.page_index * this.num_per_page + 1;
                    var to = (this.page_index + 1) * this.num_per_page;
                    if (this.local_num_images && to > this.local_num_images)
                        to = this.local_num_images;
                    if (from >= to)
                        this.item_interval = to.toString();
                    else
                        this.item_interval = from + ' - ' + to;
                }
            } else {
                this.item_interval = '0';
            }
        },
        
        moveToPrevPage() {
            this.page_index--;
            this.fetchImages();
        },
        
        moveToPage(page) {
            this.page_index = page - 1;
            this.fetchImages();
        },
        
        moveToNextPage() {
            this.page_index++;
            this.fetchImages();
        },
        
        reloadPagination() {
            this.page_index = 0; // Reset to first page when changing per_page
            this.fetchImages();
        },
        
        openUploadModal() {
            this.modal_action = 'upload';
            this.modal_image = {
                name: '',
                description: '',
                image_classification: 'mission_in_action',
                keywords: [],
                campaign_specific: false, // Always false for image library uploads
            };
            this.uploadImageUrl = '';
            this.uploadPendingUrls = [];
            this.uploadDropzoneKey = 0;
            this.uploadProgress = null;
            this.keywordsString = '';
            const modal = new bootstrap.Modal(document.getElementById('imageModal'));
            modal.show();
        },
        handleUploadFileUploaded(value) {
            if (value && value !== '') {
                const url = this.normalizeUploadUrl(value);
                this.uploadPendingUrls.push({ url, key: 'upload_' + Date.now() + '_' + Math.random().toString(36).slice(2) });
                this.uploadImageUrl = '';
                if (!this.uploadProgress) {
                    this.uploadDropzoneKey += 1;
                }
            }
        },
        handleUploadProgress(payload) {
            this.uploadProgress = payload;
            if (payload === null) {
                this.uploadDropzoneKey += 1;
            }
        },
        removePendingUploadUrl(index) {
            this.uploadPendingUrls.splice(index, 1);
        },
        normalizeUploadUrl(value) {
            if (!value) return '';
            let url = String(value).replace('%2F', '/');
            if (!url.startsWith('http')) url = 'https:' + url;
            return url;
        },
        
        editImage(image) {
            this.modal_action = 'edit';
            this.modal_image = { ...image };
            this.keywordsString = Array.isArray(image.keywords) ? image.keywords.join(', ') : '';
            this.uploadFile = null;
            const modal = new bootstrap.Modal(document.getElementById('imageModal'));
            modal.show();
            this.$nextTick(() => {
                this.adjustTextareaHeight();
            });
        },
        
        async saveImage() {
            if (this.modal_action === 'upload' && this.uploadPendingUrls.length === 0) {
                this.showNotification({
                    icon: '/img/icons/avid-icons/alert-circle.svg',
                    text: 'No images selected. Please upload one or more images.'
                });
                return;
            }

            if (this.modal_action === 'edit' && !this.modal_image.image_classification) {
                this.showNotification({
                    icon: '/img/icons/avid-icons/alert-circle.svg',
                    text: 'Missing required fields. Please select a classification.'
                });
                return;
            }

            if (this.modal_action === 'edit' && !this.modal_image.name) {
                this.showNotification({
                    icon: '/img/icons/avid-icons/alert-circle.svg',
                    text: 'Missing required fields. Please fill in all required fields.'
                });
                return;
            }

            this.uploading = true;
            this.uploadingCount = this.modal_action === 'upload' ? this.uploadPendingUrls.length : 1;

            try {
                if (this.modal_action === 'upload') {
                    let added = 0;
                    let failed = 0;

                    for (const item of this.uploadPendingUrls) {
                        try {
                            const response = await window.axios.post(`/api/client/${this.client.url}/images`, {
                                image_url: this.normalizeUploadUrl(item.url),
                                image_classification: null,
                                campaign_specific: false,
                            });

                            if (response.data && response.data.image) {
                                const newImage = response.data.image;
                                if (!newImage.public_url && newImage.gcs_path) {
                                    newImage.public_url = this.normalizeUploadUrl(item.url);
                                } else if (!newImage.public_url) {
                                    newImage.public_url = this.normalizeUploadUrl(item.url);
                                }

                                if (this.local_num_images >= 10) {
                                    try {
                                        const lastPage = Math.ceil(this.local_num_images / 1);
                                        const lastPageResponse = await window.axios.get(`/api/client/${this.client.url}/images`, {
                                            params: { per_page: 1, page: lastPage },
                                        });
                                        if (lastPageResponse.data?.data?.length > 0) {
                                            const oldestImage = lastPageResponse.data.data[0];
                                            if (oldestImage?.id) {
                                                await window.axios.delete(`/api/client/${this.client.url}/images/${oldestImage.id}`);
                                                this.local_num_images--;
                                                const indexInView = this.images.findIndex(img => img.id === oldestImage.id);
                                                if (indexInView !== -1) this.images.splice(indexInView, 1);
                                            }
                                        }
                                    } catch (err) {
                                        console.error('Error deleting oldest image:', err);
                                    }
                                }

                                this.images.unshift(newImage);
                                this.local_num_images++;
                                added++;
                                if (this.images.length > this.num_per_page) this.images.pop();
                            }
                        } catch (err) {
                            console.error('Error uploading image:', err);
                            failed++;
                        }
                    }

                    this.updatePagination();
                    this.uploadPendingUrls = [];
                    this.uploadImageUrl = '';
                    this.uploadDropzoneKey += 1;

                    const modal = bootstrap.Modal.getInstance(document.getElementById('imageModal'));
                    if (modal) modal.hide();

                    if (failed > 0) {
                        this.showNotification({
                            icon: '/img/icons/avid-icons/alert-circle.svg',
                            text: `${added} image(s) uploaded; ${failed} failed.`
                        });
                    } else {
                        this.showNotification({
                            icon: '/img/icons/nav-icons/Settings.svg',
                            text: added === 1 ? 'Image uploaded successfully.' : `${added} images uploaded successfully.`
                        });
                    }
                } else {
                    // Parse keywords for edit
                    const keywords = this.keywordsString
                        .split(',')
                        .map(k => k.trim())
                        .filter(k => k.length > 0);

                    await window.axios.put(`/api/client/${this.client.url}/images/${this.modal_image.id}`, {
                        name: this.modal_image.name,
                        description: this.modal_image.description || '',
                        keywords: keywords,
                        image_classification: this.modal_image.image_classification,
                    });

                    // Refresh list
                    await this.fetchImages();

                    const modal = bootstrap.Modal.getInstance(document.getElementById('imageModal'));
                    if (modal) modal.hide();
                    this.showNotification({
                        icon: '/img/icons/nav-icons/Settings.svg',
                        text: 'Image updated successfully.'
                    });
                }
            } catch (error) {
                console.error('Error saving image:', error);
                this.showNotification({
                    icon: '/img/icons/avid-icons/danger.svg',
                    text: `Failed to ${this.modal_action === 'upload' ? 'upload' : 'update'} image.`
                });
            } finally {
                this.uploading = false;
            }
        },
        
        async deleteImage(image) {
            const result = await Swal.fire({
                title: 'Are you sure you want to delete this image?',
                text: 'This action cannot be undone.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, delete',
                customClass: {
                    popup: 'swal-warning'
                },
            });
            
            if (result.isConfirmed) {
                try {
                    await window.axios.delete(`/api/client/${this.client.url}/images/${image.id}`);
                    
                    // Close modal if it's open
                    const modal = bootstrap.Modal.getInstance(document.getElementById('imageModal'));
                    if (modal) {
                        modal.hide();
                    }
                    
                    await this.fetchImages();

                    this.showNotification({
                        text: 'Your image was successfully deleted!'
                    });
                } catch (error) {
                    console.error('Error deleting image:', error);
                    this.showNotification({
                        icon: '/img/icons/avid-icons/danger.svg',
                        text: 'There was an error deleting the image.'
                    });
                }
            }
        },
        
        getImageUrl(image) {
            // Return the public_url from the API response
            return image.public_url || '';
        },
        
        formatClassification(classification) {
            return classification.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        },
        
        truncate(str, length) {
            if (!str) return '';
            return str.length > length ? str.substring(0, length) + '...' : str;
        },
        
        clearAllFilters() {
            this.selectedClassification = 'all';
            this.page_index = 0;
            this.fetchImages();
        },
        
        adjustTextareaHeight() {
            this.$nextTick(() => {
                const textarea = this.$refs.descriptionTextarea;
                if (textarea && this.modal_action === 'edit' && this.modal_image.description) {
                    // Reset height to auto to get accurate scrollHeight
                    textarea.style.height = 'auto';
                    // Set height based on content, with a minimum
                    const newHeight = Math.max(textarea.scrollHeight, 75); // 75px is roughly 3 rows
                    textarea.style.height = newHeight + 'px';
                }
            });
        },
        
        async updateImagePreference() {
            if (!this.canEdit('settings')) {
                return;
            }
            
            this.savingPreference = true;
            
            try {
                // Send the current client data along with the updated image settings
                const updateData = {
                    ...this.client,
                    image_preference: this.local_image_preference,
                    image_generation_instructions: this.local_image_preference === 'ai_allowed' ? (this.local_image_generation_instructions || null) : null,
                };
                
                await window.axios.put(`/api/client/${this.client.url}`, updateData);
                
                // Update the client prop if it's reactive
                if (this.client.image_preference !== undefined) {
                    this.client.image_preference = this.local_image_preference;
                }
                if (this.client.image_generation_instructions !== undefined) {
                    this.client.image_generation_instructions = this.local_image_generation_instructions || null;
                }
                
                this.showNotification({
                    icon: '/img/icons/nav-icons/Settings.svg',
                    text: 'Image preference updated successfully.'
                });
            } catch (error) {
                console.error('Error updating image preference:', error);
                // Revert to original values on error
                this.local_image_preference = this.client.image_preference || 'none';
                this.local_image_generation_instructions = this.client.image_generation_instructions || '';
                this.showNotification({
                    icon: '/img/icons/avid-icons/danger.svg',
                    text: 'Failed to update image preference.'
                });
            } finally {
                this.savingPreference = false;
            }
        },
        
        subscribeToEvents() {
            if (window.Echo && this.client && this.client.id) {
                this.echoChannel = window.Echo.private('client.' + this.client.id)
                    .listen('ClientImageProcessed', (e) => {
                        this.handleClientImageProcessed(e.image);
                    });
            }
        },
        
        handleClientImageProcessed(processedImage) {
            // Find the image in the images array and update it with processed data
            const imageIndex = this.images.findIndex(img => img.id === processedImage.id);
            
            if (imageIndex > -1) {
                // Update the image with processed data (including public_url from gcs_path)
                this.images[imageIndex] = {
                    ...processedImage,
                    public_url: processedImage.public_url || (processedImage.gcs_path ? 'https://app.avidai.com/' + processedImage.gcs_path : null)
                };
                
                // Force update
                this.$forceUpdate();
            } else {
                // If image not in current view, refresh the list to include it
                this.fetchImages();
            }
        },
    },
    watch: {
        'client.image_preference'(newVal) {
            this.local_image_preference = newVal || 'none';
        },
        'client.image_generation_instructions'(newVal) {
            this.local_image_generation_instructions = newVal || '';
        },
    }
});
</script>

<style scoped>
.image-thumbnail-wrapper {
    transition: transform 0.2s;
}

.image-thumbnail-wrapper:hover {
    transform: translateY(-2px);
}

.image-thumbnail {
    transition: opacity 0.2s;
}

.image-thumbnail:hover {
    opacity: 0.9;
}

.image-library-upload-thumb {
    width: 80px;
    height: 80px;
}

.image-library-upload-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}
</style>

